// src/context/AuthContext.jsx - UPDATED with Professor + HOD support
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, tokenManager } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = tokenManager.getAccessToken();
      if (token) {
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);

      // Store tokens
      tokenManager.setTokens(response.tokens.access, response.tokens.refresh);

      // Store user data
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      setIsAuthenticated(true);

      return {
        success: true,
        user: response.user,
      };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.error ||
                          error.response?.data?.non_field_errors?.[0] ||
                          'Login failed. Please check your credentials.';

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);

      // Check if registration requires approval
      if (response.requiresApproval) {
        return {
          success: true,
          requiresApproval: true,
          user: response.user,
          message: response.message,
        };
      }

      // Auto-approved (e.g., admin role) - store tokens and login
      if (response.tokens) {
        tokenManager.setTokens(response.tokens.access, response.tokens.refresh);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        setIsAuthenticated(true);
      }

      return {
        success: true,
        requiresApproval: false,
        user: response.user,
        message: response.message,
      };
    } catch (error) {
      console.error('Registration error:', error);

      // Handle validation errors
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for field-specific errors
        if (typeof errorData === 'object') {
          const firstError = Object.values(errorData)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;

          return {
            success: false,
            error: errorMessage || 'Registration failed. Please check your information.',
          };
        }

        return {
          success: false,
          error: errorData.detail || errorData.message || 'Registration failed.',
        };
      }

      return {
        success: false,
        error: 'Registration failed. Please try again.',
      };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        await authAPI.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  // Helper function to get dashboard route for a user
  const getDashboardRoute = (user) => {
    if (!user) return '/login';

    const userSubrole = user.profile?.faculty_subrole;

    // Professor + HOD defaults to faculty dashboard (can switch to HOD view)
    if (user.role === 'FACULTY' && userSubrole === 'PROFESSOR_HOD') {
      return '/faculty-dashboard';
    }

    // Check if user is HOD (faculty with HEAD_OF_DEPARTMENT subrole)
    if (user.role === 'FACULTY' && userSubrole === 'HEAD_OF_DEPARTMENT') {
      return '/hod-dashboard';
    }

    // Regular role-based routing
    const roleRoutes = {
      ADMIN: '/admin-dashboard',
      STUDENT: '/student-dashboard',
      FACULTY: '/faculty-dashboard',
      FINANCE: '/finance-dashboard',
      HOSTEL_ADMIN: '/hostel-dashboard',
      LIBRARY_STAFF: '/library-dashboard',
      TRANSPORT_MANAGER: '/transport-dashboard',
      recepsonist: '/reception',
      PRINCIPAL:         '/principal-dashboard',
      COE:               '/coe-dashboard',
      PAPER_EVALUATOR:   '/paper-evaluator-dashboard',
    };

    return roleRoutes[user.role] || '/login';
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    checkAuth,
    getDashboardRoute,
  };

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div className="spinner-large"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;